// clientCompany.js — what the Reports list's "Client company" picker offers and says.
// ---------------------------------------------------------------------------
// 2026-11-04 (v0.330.0). Brett: "Jane would need to be a client." and
// "Clients dont see trade stuff, trades dont see clients." A report reaches a
// client person's DropPin home only when it is stamped for their company AND
// shared with them by name (api/shared/client-home.js). This replaces the old
// "Visible to the client" tick, which showed a report to the whole company.
//
// Every state is named (never a bare empty list):
//   loading  - the list is being read; the picker waits
//   failed   - the list could not be read; says so, offers Try again
//   none     - read, and no client company is linked to this builder yet
//   ok       - read, with companies to pick
// A report stamped for a company that is not in the list (unlinked since)
// keeps that stamp on screen as "Client #id (not linked)" - never shown as
// "No client", which would hide what the report is actually stamped for.

export function stampOf(report) {
  const v = report ? report.clientWorkspaceId : null;
  const n = Number(v);
  return v != null && v !== '' && Number.isInteger(n) && n > 0 ? n : null;
}

export function listState(clients) {
  if (!clients || clients.state === 'loading') return 'loading';
  if (clients.state === 'failed') return 'failed';
  return Array.isArray(clients.list) && clients.list.length ? 'ok' : 'none';
}

// The <option>s: "No client" first, then the linked companies by name, then
// the report's own stamp if it is not among them.
export function clientOptions(clients, stamp) {
  const list = (clients && Array.isArray(clients.list)) ? clients.list : [];
  const out = [{ value: '', label: 'No client' }];
  const seen = new Set();
  for (const c of list) {
    const id = Number(c && c.id);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    out.push({ value: String(id), label: String((c.name || '').trim() || `Client #${id}`) });
  }
  if (stamp != null && !seen.has(stamp)) {
    const known = listState(clients) === 'ok' || listState(clients) === 'none';
    out.push({ value: String(stamp), label: known ? `Client #${stamp} (not linked)` : `Client #${stamp}`, stale: known });
  }
  return out;
}

export function companyName(clients, stamp) {
  if (stamp == null) return null;
  const hit = ((clients && clients.list) || []).find((c) => Number(c.id) === stamp);
  return hit ? String(hit.name || '').trim() || `Client #${stamp}` : null;
}

// The line under the picker, in words.
export function stampLine(clients, stamp) {
  const st = listState(clients);
  if (stamp == null) {
    if (st === 'none') return 'No client. No client companies are linked to you yet - one appears here once its people open DropPin from your InSpec.';
    return 'No client - only the people you share it with can open it.';
  }
  const name = companyName(clients, stamp);
  if (name) return `For ${name}: its people see it in their DropPin only once you share it with them by name.`;
  if (st === 'loading') return 'Reading your client companies…';
  if (st === 'failed') return `Stamped for client #${stamp}. Your client companies could not be read to name it.`;
  return `Stamped for client #${stamp}, which is no longer linked to you - no one there sees it in their DropPin. Pick another or No client.`;
}

// The PATCH body for a pick ('' = clear).
export function stampBody(value) {
  if (value === '' || value == null) return { clientWorkspaceId: null };
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) throw new Error('Not a client company.');
  return { clientWorkspaceId: n };
}

// Why a stamp did not change, in words.
export function stampRefusal(err) {
  if (err && err.offline) return 'You are offline - the client company was not changed. Try again when you are back online.';
  const s = err && err.status;
  const said = err && err.body && err.body.error;
  if (s === 403) return said || 'That company is not one of your clients in DropPin. Nothing was changed.';
  if (s === 503) return said || 'DropPin could not check your client companies just now. Nothing was changed - try again shortly.';
  if (s === 400) return said || 'That is not a client company. Nothing was changed.';
  const words = said || (err && err.message) || 'The client company could not be changed.';
  return /nothing was changed/i.test(words) ? words : `${words} Nothing was changed.`;
}
